import React, { useState, useEffect } from 'react';
import { LineChart, Line, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, Database, Code, Server, Mail, Linkedin, Github, ExternalLink, Calendar, Brain } from 'lucide-react';
import { InlineWidget } from 'react-calendly';

function HomePage() {
  const [cryptoData, setCryptoData] = useState({
    bitcoin: { price: 0, change: 0, history: [] },
    ethereum: { price: 0, change: 0, history: [] },
    solana: { price: 0, change: 0, history: [] }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCryptoData = async () => {
      try {
        const response = await fetch(
          'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum,solana&vs_currencies=usd&include_24hr_change=true'
        );
        const data = await response.json();

        setCryptoData(prev => ({
          bitcoin: {
            price: data.bitcoin.usd,
            change: data.bitcoin.usd_24h_change,
            history: [...prev.bitcoin.history, { time: new Date().toLocaleTimeString(), price: data.bitcoin.usd }].slice(-10)
          },
          ethereum: {
            price: data.ethereum.usd,
            change: data.ethereum.usd_24h_change,
            history: [...prev.ethereum.history, { time: new Date().toLocaleTimeString(), price: data.ethereum.usd }].slice(-10)
          },
          solana: {
            price: data.solana.usd,
            change: data.solana.usd_24h_change,
            history: [...prev.solana.history, { time: new Date().toLocaleTimeString(), price: data.solana.usd }].slice(-10)
          }
        }));
        setLoading(false);
      } catch (error) {
        console.error('Error fetching crypto data:', error);
        setLoading(false);
      }
    };

    fetchCryptoData();
    const interval = setInterval(fetchCryptoData, 30000);

    return () => clearInterval(interval);
  }, []);

  const CryptoCard = ({ name, symbol, data }) => (
    <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-cyan-500/20 hover:border-cyan-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/20">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-xl font-bold text-white mb-1">{name}</h3>
          <p className="text-gray-400 text-sm uppercase">{symbol}</p>
        </div>
        <div className={`flex items-center gap-1 px-3 py-1 rounded-full ${data.change >= 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
          <TrendingUp className={`w-4 h-4 ${data.change < 0 ? 'rotate-180' : ''}`} />
          <span className="text-sm font-semibold">{Math.abs(data.change).toFixed(2)}%</span>
        </div>
      </div>
      <div className="text-3xl font-bold text-cyan-400 mb-4">
        ${data.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </div>
      {data.history.length > 1 && (
        <ResponsiveContainer width="100%" height={100}>
          <LineChart data={data.history}>
            <Line type="monotone" dataKey="price" stroke="#00D9FF" strokeWidth={2} dot={false} />
            <Tooltip
              contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #00D9FF', borderRadius: '8px' }}
              labelStyle={{ color: '#9CA3AF' }}
              itemStyle={{ color: '#00D9FF' }}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      {/* Hero Section */}
      <section className="container mx-auto px-4 md:px-6 py-12 md:py-20">
        <div className="flex flex-col md:flex-row items-center gap-12">
          <div className="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 rounded-full overflow-hidden shadow-2xl shadow-cyan-500/50 ring-4 ring-cyan-500/30">
            <img
              src="/profile.png"
              alt="Phillip Neiman"
              className="w-full h-full object-cover"
              style={{ objectPosition: 'center 10%' }}
            />
          </div>
          <div className="flex-1 text-center md:text-left">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Phillip Neiman
            </h1>
            <p className="text-xl sm:text-2xl md:text-3xl text-gray-300 mb-6">Data Engineer and AI Specialist</p>
            <p className="text-lg text-gray-400 max-w-2xl">
              Scalable data pipelines, cost-effective cloud storage, RAG architecture driving quicker time to insight and your company's future agentic AI and ML operations.
            </p>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="container mx-auto px-4 md:px-6">
        <div className="border-t border-cyan-500/30"></div>
      </div>

      {/* Schedule a Meeting Section */}
      <section className="container mx-auto px-4 md:px-6 py-10 md:py-16">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Calendar className="w-8 h-8 text-cyan-400" />
            <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Schedule a Meeting
            </h2>
          </div>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Book a consultation to discuss your data engineering needs and how I can help transform your data infrastructure.
          </p>
        </div>
        <div className="max-w-4xl mx-auto bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-4 border border-cyan-500/20">
          <InlineWidget
            url="https://calendly.com/phillipneiman/30-minute-meeting"
            styles={{
              height: '900px',
              minWidth: '100%'
            }}
          />
        </div>
      </section>

      {/* Divider */}
      <div className="container mx-auto px-4 md:px-6">
        <div className="border-t border-cyan-500/30"></div>
      </div>

      {/* Live Crypto Analytics Section */}
      <section className="container mx-auto px-4 md:px-6 py-10 md:py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
            Live Crypto Analytics
          </h2>
          <p className="text-gray-400">Real-time data updates every 30 seconds</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
            <p className="mt-4 text-gray-400">Loading crypto data...</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            <CryptoCard name="Bitcoin" symbol="BTC" data={cryptoData.bitcoin} />
            <CryptoCard name="Ethereum" symbol="ETH" data={cryptoData.ethereum} />
            <CryptoCard name="Solana" symbol="SOL" data={cryptoData.solana} />
          </div>
        )}
      </section>

      {/* Divider */}
      <div className="container mx-auto px-4 md:px-6">
        <div className="border-t border-cyan-500/30"></div>
      </div>

      {/* Services Section */}
      <section className="container mx-auto px-4 md:px-6 py-10 md:py-16">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
          Services
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-8 border border-cyan-500/20 hover:border-cyan-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/20">
            <Database className="w-12 h-12 text-cyan-400 mb-4" />
            <h3 className="text-2xl font-bold mb-3">Data Pipeline Engineering</h3>
            <p className="text-gray-400">
              Design and build robust ETL/ELT pipelines for efficient data processing and transformation at scale.
            </p>
          </div>
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-8 border border-cyan-500/20 hover:border-cyan-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/20">
            <Code className="w-12 h-12 text-cyan-400 mb-4" />
            <h3 className="text-2xl font-bold mb-3">Advanced Analytics</h3>
            <p className="text-gray-400">
              Implement advanced end-to-end data solutions and dashboards for instant business insights.
            </p>
          </div>
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-8 border border-cyan-500/20 hover:border-cyan-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/20">
            <Server className="w-12 h-12 text-cyan-400 mb-4" />
            <h3 className="text-2xl font-bold mb-3">Data Infrastructure</h3>
            <p className="text-gray-400">
              Architect and optimize data warehouses, lakes, and cloud infrastructure for maximum performance.
            </p>
          </div>
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-8 border border-cyan-500/20 hover:border-cyan-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/20">
            <Brain className="w-12 h-12 text-cyan-400 mb-4" />
            <h3 className="text-2xl font-bold mb-3">Agentic AI & ML Operations</h3>
            <p className="text-gray-400">
              Build intelligent AI agents and deploy machine learning models with automated operations and continuous optimization.
            </p>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="container mx-auto px-4 md:px-6">
        <div className="border-t border-cyan-500/30"></div>
      </div>

      {/* Tech Stack Section */}
      <section className="container mx-auto px-4 md:px-6 py-10 md:py-16">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
          Tech Stack
        </h2>
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-8 border border-cyan-500/20">
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <h3 className="text-lg font-bold text-cyan-400 mb-3">Languages</h3>
              <ul className="text-gray-300 space-y-2">
                <li>Python</li>
                <li>SQL</li>
                <li>JavaScript</li>
                <li>Scala</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-bold text-cyan-400 mb-3">Big Data</h3>
              <ul className="text-gray-300 space-y-2">
                <li>Databricks</li>
                <li>Apache Spark</li>
                <li>Kafka</li>
                <li>Airflow</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-bold text-cyan-400 mb-3">Cloud & DB</h3>
              <ul className="text-gray-300 space-y-2">
                <li>Azure</li>
                <li>Fabric</li>
                <li>PostgreSQL</li>
                <li>Supabase</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-bold text-cyan-400 mb-3">Tools</h3>
              <ul className="text-gray-300 space-y-2">
                <li>Docker</li>
                <li>Kubernetes</li>
                <li>Git</li>
                <li>dbt</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="container mx-auto px-4 md:px-6">
        <div className="border-t border-cyan-500/30"></div>
      </div>

      {/* Contact Section */}
      <section className="container mx-auto px-4 md:px-6 py-10 md:py-16">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
          Get In Touch
        </h2>
        <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-6 px-4">
          <a
            href="mailto:your.email@example.com"
            className="flex items-center gap-2 bg-gradient-to-br from-gray-800 to-gray-900 px-6 py-3 rounded-lg border border-cyan-500/20 hover:border-cyan-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/20"
          >
            <Mail className="w-5 h-5 text-cyan-400" />
            <span>Email</span>
            <ExternalLink className="w-4 h-4" />
          </a>
          <a
            href="https://linkedin.com/in/yourprofile"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-gradient-to-br from-gray-800 to-gray-900 px-6 py-3 rounded-lg border border-cyan-500/20 hover:border-cyan-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/20"
          >
            <Linkedin className="w-5 h-5 text-cyan-400" />
            <span>LinkedIn</span>
            <ExternalLink className="w-4 h-4" />
          </a>
          <a
            href="https://github.com/yourprofile"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-gradient-to-br from-gray-800 to-gray-900 px-6 py-3 rounded-lg border border-cyan-500/20 hover:border-cyan-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/20"
          >
            <Github className="w-5 h-5 text-cyan-400" />
            <span>GitHub</span>
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-8">
        <div className="container mx-auto px-4 md:px-6 text-center text-gray-400">
          <p>&copy; 2026 Phillip Neiman. All rights reserved.</p>
          <p className="mt-2 text-sm">Built with React, Tailwind CSS, and CoinGecko API</p>
        </div>
      </footer>
    </div>
  );
}

export default HomePage;
